import { memo, useEffect, useRef } from 'react';
import { Clock, LogOut, Shield } from 'lucide-react';
import { IDLE_TIMEOUT_MINUTES } from '@/lib/signOutReason';

interface IdleWarningDialogProps {
  open: boolean;
  secondsLeft: number;
  onStay: () => void;
  onLogout: () => void;
}

/**
 * Warning dialog shown before auto-logout due to inactivity.
 * Displays a countdown and gives the user a chance to stay logged in.
 */
export const IdleWarningDialog = memo(function IdleWarningDialog({
  open,
  secondsLeft,
  onStay,
  onLogout,
}: IdleWarningDialogProps) {
  // Retitle the tab while the warning is open, so it is noticed from another window or tab. A fixed title
  // rather than a ticking countdown: Chrome slows a background tab's timers to once a minute, and a
  // background tab is exactly where this has to show. Restored whenever the dialog closes, including when
  // activity in another tab closes it.
  const savedTitle = useRef('');
  useEffect(() => {
    if (!open) return;
    savedTitle.current = document.title;
    document.title = '⚠ Logging out soon';
    return () => {
      document.title = savedTitle.current;
    };
  }, [open]);

  if (!open) return null;

  const minutes = Math.floor(secondsLeft / 60);
  const seconds = secondsLeft % 60;
  const timeStr = minutes > 0
    ? `${minutes}:${seconds.toString().padStart(2, '0')}`
    : `${seconds}s`;

  // Progress from 1 (full time) to 0 (expired)
  const progress = secondsLeft / 60;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="idle-title"
        aria-describedby="idle-desc"
        className="mx-4 w-full max-w-sm animate-dialog-in rounded-2xl border bg-card shadow-2xl"
      >
        <div className="p-6 text-center">
          {/* Icon with animated ring */}
          <div className="relative mx-auto h-16 w-16">
            <svg className="h-16 w-16 -rotate-90" viewBox="0 0 64 64">
              <circle
                cx="32"
                cy="32"
                r="28"
                fill="none"
                stroke="currentColor"
                strokeWidth="3"
                className="text-muted/50"
              />
              <circle
                cx="32"
                cy="32"
                r="28"
                fill="none"
                stroke="currentColor"
                strokeWidth="3"
                strokeDasharray={2 * Math.PI * 28}
                strokeDashoffset={2 * Math.PI * 28 * (1 - progress)}
                strokeLinecap="round"
                className="text-warning transition-all duration-1000"
              />
            </svg>
            <div className="absolute inset-0 flex items-center justify-center">
              <Clock className="h-6 w-6 text-warning" />
            </div>
          </div>

          <h3 id="idle-title" className="mt-4 text-lg font-semibold text-foreground">
            Session Expiring
          </h3>
          <p id="idle-desc" className="mt-2 text-sm text-muted-foreground leading-relaxed">
            You&apos;ve been inactive for {IDLE_TIMEOUT_MINUTES - 1} minutes. For security, you&apos;ll be logged out in{' '}
            <span className="font-semibold text-warning">{timeStr}</span>.
          </p>

          <div className="mt-2 flex items-center justify-center gap-1.5 text-xs text-muted-foreground/60">
            <Shield className="h-3 w-3" />
            <span>Security policy — auto-logout on idle</span>
          </div>
        </div>

        <div className="flex gap-2 border-t px-6 py-4">
          <button
            onClick={onLogout}
            className="flex flex-1 items-center justify-center gap-2 rounded-lg border px-4 py-2.5 text-sm font-medium text-muted-foreground transition-colors hover:bg-accent"
          >
            <LogOut className="h-4 w-4" />
            Log out now
          </button>
          <button
            onClick={onStay}
            autoFocus
            className="flex flex-1 items-center justify-center gap-2 rounded-lg bg-primary px-4 py-2.5 text-sm font-medium text-primary-foreground shadow-sm transition-colors hover:bg-primary/90 press-scale"
          >
            Stay logged in
          </button>
        </div>
      </div>
    </div>
  );
});
